import { useMemo, useState } from "react";
import { Radar, Workflow, Bell, LayoutDashboard } from "lucide-react";
import { useSiteAssets } from "@/hooks/useSiteAssets";

type TabKey = "visitor-intelligence" | "lead-pipeline" | "follow-up-center" | "executive-dashboard";

const META: Record<TabKey, { icon: React.ElementType; label: string; benefit: string }> = {
  "visitor-intelligence": { icon: Radar, label: "Visitor Intelligence", benefit: "See which prospects are on your site right now, and how hot they are." },
  "lead-pipeline": { icon: Workflow, label: "Lead Pipeline", benefit: "Every lead, every stage, every dollar in pipeline, at a glance." },
  "follow-up-center": { icon: Bell, label: "Follow-Up Center", benefit: "No lead falls through. Tasks, overdue alerts, and snooze in one place." },
  "executive-dashboard": { icon: LayoutDashboard, label: "Executive Dashboard", benefit: "Daily owner briefing: wins, risks, and what needs you today." },
};

/**
 * Hidden publicly until at least one dashboard screenshot is uploaded in
 * Admin → Assets. Never renders placeholder imagery.
 */
const DashboardShowcase = () => {
  const { data: assets = [] } = useSiteAssets("dashboard_screenshot");
  const shots = useMemo(() => assets.map((a) => ({
    id: a.id,
    key: ((a.metadata?.key as TabKey) || "executive-dashboard") as TabKey,
    label: a.label || META[((a.metadata?.key as TabKey) || "executive-dashboard")].label,
    caption: a.caption || "",
    imageUrl: a.file_url,
  })), [assets]);

  const [active, setActive] = useState(0);
  if (shots.length === 0) return null;

  const current = shots[Math.min(active, shots.length - 1)];
  const meta = META[current.key];
  const Icon = meta?.icon || LayoutDashboard;

  return (
    <section className="section-padding">
      <div className="container-narrow">
        <div className="max-w-2xl">
          <div className="gold-divider" />
          <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
            Visibility Into Every Lead
          </h2>
          <p className="mt-3 text-muted-foreground">
            Built-in operator dashboards give you full visibility from first visit to signed deal, no spreadsheets, no guesswork.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="flex flex-row gap-2 overflow-x-auto lg:flex-col lg:gap-2">
            {shots.map((s, i) => {
              const TabIcon = META[s.key]?.icon || LayoutDashboard;
              const isActive = i === active;
              return (
                <button key={s.id} onClick={() => setActive(i)}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${isActive ? "border-secondary bg-secondary/10 text-foreground" : "border-border bg-card text-muted-foreground hover:border-secondary/50 hover:text-foreground"}`}>
                  <TabIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-secondary" : ""}`} />
                  <span className="text-sm font-semibold whitespace-nowrap lg:whitespace-normal">{s.label}</span>
                </button>
              );
            })}
          </div>

          <div>
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl shadow-primary/5">
              <img src={current.imageUrl} alt={`${current.label}, real screenshot from the Elevate operator platform`} loading="lazy" className="block h-auto w-full" />
            </div>
            <div className="mt-4 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-foreground">{current.label}</div>
                <p className="mt-1 text-sm text-muted-foreground">{current.caption || meta?.benefit}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardShowcase;
